<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { clampScore } from './buzzScoreDistribution';
import {
  buildCalculatorLines,
  computeCompositeScore,
  formatCalcValue,
} from './buzzScoreFormula';
import { buzzSignalStyle } from './buzzSignalColors';
import BuzzSignalWeightIndicator from './BuzzSignalWeightIndicator.vue';

const props = defineProps({
  summary: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const lineLabels = computed(() => ({
  reviews: t('BUZZ_REPORTS.CATEGORY_REVIEWS'),
  media: t('BUZZ_REPORTS.CATEGORY_MEDIA'),
  footfall: t('BUZZ_REPORTS.CATEGORY_FOOTFALL'),
}));

const contributionLines = computed(() => {
  const reviews = clampScore(props.summary?.b_reviews);
  const media = clampScore(props.summary?.b_media);
  const footfall = clampScore(props.summary?.b_footfall);

  return buildCalculatorLines(reviews, media, footfall).map(line => ({
    ...line,
    label: lineLabels.value[line.key],
    style: buzzSignalStyle(line.key),
  }));
});

const buzzScore = computed(() =>
  computeCompositeScore(
    clampScore(props.summary?.b_reviews),
    clampScore(props.summary?.b_media),
    clampScore(props.summary?.b_footfall)
  )
);

const hasData = computed(() =>
  contributionLines.value.some(line => line.score != null)
);
</script>

<template>
  <div
    class="w-full flex flex-col m-0.5 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 overflow-hidden"
  >
    <div class="px-4 pt-2.5 pb-1">
      <p class="text-xs font-medium text-n-slate-12 mb-0">
        {{ t('BUZZ_REPORTS.FORMULA.TITLE') }}
      </p>
    </div>

    <div v-if="!hasData" class="px-4 pb-3 text-xs text-n-slate-10">
      {{ t('BUZZ_REPORTS.SIGNALS.NO_DATA') }}
    </div>

    <div v-else class="flex flex-col gap-1 px-4 pb-2.5">
      <div
        v-for="line in contributionLines"
        :key="line.key"
        class="flex items-center justify-between gap-2"
      >
        <span
          class="text-[11px] flex items-center gap-1 min-w-0"
          :class="line.style.textClass"
        >
          <span
            class="h-1.5 w-1.5 shrink-0 rounded-full"
            :class="line.style.dotClass"
          />
          <span class="truncate">{{ line.label }}</span>
        </span>
        <div class="flex items-center gap-1.5 shrink-0">
          <BuzzSignalWeightIndicator
            :weight="line.weight"
            :bar-class="line.style.dotClass"
          />
          <span
            class="font-mono text-xs font-medium tabular-nums w-9 text-right"
            :class="line.style.textClass"
          >
            {{ formatCalcValue(line.product) }}
          </span>
        </div>
      </div>

      <div
        class="border-t border-n-container pt-1.5 flex items-center justify-between gap-2"
      >
        <span class="text-[11px] font-medium text-n-slate-12">
          {{ t('BUZZ_REPORTS.SCORE') }}
        </span>
        <span
          class="font-mono text-xs font-semibold text-n-slate-12 tabular-nums"
        >
          {{ formatCalcValue(buzzScore) }}
        </span>
      </div>
    </div>
  </div>
</template>
